<script setup>
import { computed } from 'vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import ButtonCopyToClipboard from '@/components/Moleculas/ButtonCopyToClipboard.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconLinkExternal from '@/components/icon/IconLinkExternal.vue'
import { TX_STATUS_LABELS, TX_STATUS_VARIANTS, formatMoney, refLabel, productTitle, promoCodeOf, discountOf, isRefundable, isIncomplete, unrefundableReason } from '@/Utils/siteTransactions'
import { billingLabel, periodSuffix } from '@/Utils/sitePeriod'

// Side panel with everything castr knows about one transaction (§6e
// Transaction): viewer, product, payments, refunds, receipt and the refund
// action. Opened by clicking a row on the Transactions tab.
const props = defineProps({
	modelValue: {
		type: Boolean,
		default: false,
	},
	transaction: {
		type: Object,
		default: null,
	},
	siteId: {
		type: String,
		required: true,
	},
	content: {
		type: Array,
		default: () => [],
	},
	categories: {
		type: Array,
		default: () => [],
	},
	refunding: {
		type: Boolean,
		default: false,
	},
})
const emit = defineEmits(['update:modelValue', 'refund'])

const open = computed({
	get: () => props.modelValue,
	set: (v) => emit('update:modelValue', v),
})
const tx = computed(() => props.transaction)
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—')
const viewerLabel = computed(() => tx.value?.viewer?.name || tx.value?.viewer?.email || (tx.value?.viewer?.id ? `Viewer #${tx.value.viewer.id}` : '—'))
const lists = computed(() => ({ content: props.content, categories: props.categories }))
const productLabel = computed(() => productTitle(tx.value, lists.value))
const items = computed(() => (tx.value?.item_refs || []).map((r) => refLabel(r, lists.value)))
const promoCode = computed(() => promoCodeOf(tx.value))
const discount = computed(() => discountOf(tx.value))
const statusLabel = computed(() => (isIncomplete(tx.value) ? 'Incomplete' : TX_STATUS_LABELS[tx.value?.status] || tx.value?.status || '—'))
const statusVariant = computed(() => TX_STATUS_VARIANTS[tx.value?.status] || 'muted')
const refundable = computed(() => isRefundable(tx.value))
const payments = computed(() => [...(tx.value?.payments || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)))
const refunds = computed(() => [...(tx.value?.refunds || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)))
const receiptUrl = computed(() => tx.value?.payment?.receipt_url || null)
const periodLabel = computed(() => {
	const t = tx.value
	if (!t) return ''
	return billingLabel({ recurring: t.recurring ?? t.product?.recurring, period: t.period || t.product?.period })
})
const viewerRoute = computed(() => ({ name: 'SiteViewers', params: { siteId: props.siteId }, query: tx.value?.viewer?.email ? { q: tx.value.viewer.email } : {} }))
</script>

<template>
	<ModalBox v-model="open" :title="tx ? `Transaction ${tx.public_id || `#${tx.id}`}` : 'Transaction'" placement="right" size="md">
		<div v-if="tx" class="flex flex-col gap-5 px-4 pb-4 sm:px-6 sm:pb-6" data-testid="transaction-panel">
			<div class="flex flex-wrap items-center gap-2">
				<StatusPill pill :variant="statusVariant" data-testid="transaction-panel-status">{{ statusLabel }}</StatusPill>
				<span class="text-tiny text-surface-7">{{ fmtDateTime(tx.created_at) }}</span>
				<ButtonCopyToClipboard v-if="tx.public_id" :text="tx.public_id" size="sm" fixed-width="w-auto" class="ml-auto" />
			</div>

			<div class="grid grid-cols-2 gap-3 rounded-lg bg-surface-3 p-3 text-sm">
				<div>
					<p class="mb-0.5 text-tiny text-surface-7">{{ isIncomplete(tx) ? 'Price (not charged)' : 'Collected' }}</p>
					<p class="mb-0 font-semibold" :class="isIncomplete(tx) ? 'text-surface-6' : 'text-white'" data-testid="transaction-panel-amount">
						{{ tx.free ? 'Free' : formatMoney(isIncomplete(tx) ? tx.price ?? tx.amount : tx.amount, tx.currency) }}
					</p>
				</div>
				<div>
					<p class="mb-0.5 text-tiny text-surface-7">Refunded</p>
					<p class="mb-0 font-semibold" :class="Number(tx.refunded_amount) > 0 ? 'text-warning' : 'text-surface-7'">{{ Number(tx.refunded_amount) > 0 ? formatMoney(tx.refunded_amount, tx.currency) : '—' }}</p>
				</div>
				<div>
					<p class="mb-0.5 text-tiny text-surface-7">Net</p>
					<p class="mb-0 font-semibold text-white">{{ tx.free ? '—' : formatMoney(tx.net_amount, tx.currency) }}</p>
				</div>
				<div>
					<p class="mb-0.5 text-tiny text-surface-7">Billing</p>
					<p class="mb-0 text-surface-text-1">{{ periodLabel }}<template v-if="tx.expires_at"> · until {{ fmtDateTime(tx.expires_at) }}</template></p>
				</div>
			</div>

			<section>
				<h3 class="mb-1.5 text-tiny font-semibold uppercase tracking-wider text-surface-6">Viewer</h3>
				<div class="flex items-start justify-between gap-3 rounded-lg bg-surface-3 p-3">
					<div class="min-w-0">
						<p class="mb-0 truncate text-sm text-white" data-testid="transaction-panel-viewer">{{ viewerLabel }}</p>
						<p v-if="tx.viewer?.name && tx.viewer?.email" class="mb-0 truncate text-tiny text-surface-7">{{ tx.viewer.email }}</p>
						<p v-if="tx.viewer?.id" class="mb-0 text-tiny text-surface-6">Viewer #{{ tx.viewer.id }}</p>
					</div>
					<FormButton v-if="tx.viewer?.email || tx.viewer?.id" type="link" size="sm" class="shrink-0" :to="viewerRoute">Open viewer</FormButton>
				</div>
			</section>

			<section>
				<h3 class="mb-1.5 text-tiny font-semibold uppercase tracking-wider text-surface-6">Plan</h3>
				<div class="rounded-lg bg-surface-3 p-3">
					<p class="mb-0 text-sm text-white" data-testid="transaction-panel-product">{{ productLabel }}</p>
					<p class="mb-0 text-tiny text-surface-7">
						<template v-if="tx.product?.scope === 'site'">Whole site</template>
						<template v-else-if="items.length">{{ items.join(', ') }}</template>
						<template v-else>—</template>
						<template v-if="tx.price !== undefined && tx.price !== null && !tx.free"> · {{ formatMoney(tx.price, tx.currency) }}<template v-if="tx.recurring ?? tx.product?.recurring">{{ ` ${periodSuffix(tx.period || tx.product?.period)}` }}</template></template>
					</p>
					<p v-if="promoCode" class="mb-0 mt-1.5 text-tiny text-surface-7" data-testid="transaction-panel-promo">
						Promo code <code class="rounded bg-surface-4 px-1 py-0.5 text-[11px] text-white">{{ promoCode }}</code><template v-if="discount"> · −{{ formatMoney(discount, tx.currency) }}</template>
					</p>
				</div>
			</section>

			<section>
				<h3 class="mb-1.5 text-tiny font-semibold uppercase tracking-wider text-surface-6">Payments</h3>
				<ul v-if="payments.length" class="m-0 list-none divide-y divide-surface-4 rounded-lg bg-surface-3 p-0" data-testid="transaction-panel-payments">
					<li v-for="p in payments" :key="p.id" class="flex items-center justify-between gap-3 px-3 py-2 text-sm">
						<div class="min-w-0">
							<p class="mb-0 text-white">{{ formatMoney(p.amount, p.currency || tx.currency) }}</p>
							<p class="mb-0 truncate text-tiny text-surface-7">{{ fmtDateTime(p.created_at) }}<template v-if="tx.payment?.method && tx.payment?.id === p.id"> · {{ tx.payment.method }}</template><template v-if="p.transaction_id"> · {{ p.transaction_id }}</template></p>
						</div>
						<StatusPill pill size="sm" :variant="p.status === 'paid' ? 'success' : 'muted'">{{ p.status }}</StatusPill>
					</li>
				</ul>
				<p v-else class="mb-0 rounded-lg bg-surface-3 px-3 py-2 text-tiny text-surface-7">{{ isIncomplete(tx) ? 'The viewer never completed the checkout — nothing was charged.' : tx.free ? 'Free access, no payment.' : 'No payments recorded.' }}</p>
			</section>

			<section v-if="refunds.length">
				<h3 class="mb-1.5 text-tiny font-semibold uppercase tracking-wider text-surface-6">Refunds</h3>
				<ul class="m-0 list-none divide-y divide-surface-4 rounded-lg bg-surface-3 p-0" data-testid="transaction-panel-refunds">
					<li v-for="r in refunds" :key="r.id" class="flex items-center justify-between gap-3 px-3 py-2 text-sm">
						<div class="min-w-0">
							<p class="mb-0 text-warning">−{{ formatMoney(r.amount, r.currency || tx.currency) }}</p>
							<p class="mb-0 truncate text-tiny text-surface-7">{{ fmtDateTime(r.created_at) }}<template v-if="r.reason"> · {{ r.reason }}</template></p>
						</div>
					</li>
				</ul>
			</section>

			<section>
				<h3 class="mb-1.5 text-tiny font-semibold uppercase tracking-wider text-surface-6">Receipt</h3>
				<a v-if="receiptUrl" :href="receiptUrl" target="_blank" rel="noopener" class="inline-flex items-center gap-1 text-sm text-surface-8 hover:text-white" data-testid="transaction-panel-receipt">
					Open Stripe receipt <icon-base :width="12" :height="12" fill="none"><icon-link-external /></icon-base>
				</a>
				<p v-else class="mb-0 text-tiny text-surface-7">{{ tx.payment?.method === 'stripe' ? 'Stripe emails the receipt to the viewer; a link is not available here yet.' : 'No receipt for this transaction.' }}</p>
			</section>
		</div>
		<template #footer>
			<footer v-if="tx" class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:items-center sm:p-6">
				<FormButton v-if="refundable" type="danger" class="w-full justify-center sm:w-auto" :disabled="refunding" data-testid="transaction-panel-refund" @click="emit('refund', tx)">Refund…</FormButton>
				<p v-else class="mb-0 text-tiny text-surface-7 sm:mr-auto" data-testid="transaction-panel-unrefundable">{{ unrefundableReason(tx) }}</p>
				<FormButton type="secondary" class="w-full justify-center sm:w-auto" @click="open = false">Close</FormButton>
			</footer>
		</template>
	</ModalBox>
</template>
